import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  SvgAlertTriangle,
  SvgCalendar3,
  SvgCheckCircle1,
  SvgCheckCircleHollow,
  SvgEditSkull1,
  SvgFavoriteStar,
  SvgLockClosed,
  SvgValidationCheck,
} from '@actual-app/components/icons/v2';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import type { ScheduleStatusType } from '@actual-app/core/shared/schedules';
import { titleFirst } from '@actual-app/core/shared/util';

import { useIsNocturneTheme } from '#style';
import { getStatusLabel } from '#util/schedule';

// Consists of Schedule Statuses + Transaction statuses
export type StatusTypes =
  | ScheduleStatusType
  | 'cleared'
  | 'pending'
  | 'reconciled';

export const defaultStatusProps = {
  color: theme.buttonNormalDisabledText,
  backgroundColor: theme.tableRowHeaderBackground,
  Icon: SvgCheckCircleHollow,
};

export function getStatusProps(status: StatusTypes | null | undefined) {
  switch (status) {
    case 'missed':
      return {
        color: theme.errorTextDarker,
        backgroundColor: theme.errorBackground,
        Icon: SvgEditSkull1,
      };
    case 'due':
      return {
        color: theme.warningTextDark,
        backgroundColor: theme.warningBackground,
        Icon: SvgAlertTriangle,
      };
    case 'upcoming':
      return {
        color: theme.upcomingText,
        backgroundColor: theme.upcomingBackground,
        Icon: SvgCalendar3,
      };
    case 'paid':
      return {
        color: theme.noticeText,
        backgroundColor: theme.noticeBackgroundLight,
        Icon: SvgValidationCheck,
      };
    case 'completed':
      return {
        color: theme.tableHeaderText,
        backgroundColor: theme.tableRowHeaderBackground,
        Icon: SvgFavoriteStar,
      };
    case 'pending':
      return {
        color: theme.noticeTextLight,
        backgroundColor: theme.noticeBackgroundLight,
        Icon: SvgCalendar3,
      };
    case 'scheduled':
      return {
        color: theme.tableRowHeaderText,
        backgroundColor: theme.tableRowHeaderBackground,
        Icon: SvgCalendar3,
      };
    case 'cleared':
      return {
        color: theme.noticeTextLight,
        backgroundColor: theme.tableRowHeaderBackground,
        Icon: SvgCheckCircle1,
      };
    case 'reconciled':
      return {
        color: theme.noticeTextLight,
        backgroundColor: theme.tableRowHeaderBackground,
        Icon: SvgLockClosed,
      };
    default:
      return defaultStatusProps;
  }
}

export function StatusBadge({
  status,
  isEstimate = false,
}: {
  status: ScheduleStatusType;
  isEstimate?: boolean;
}) {
  const isNocturneTheme = useIsNocturneTheme();
  const { t } = useTranslation();
  const statusProps = getStatusProps(status);
  const isFutureSchedule = status === 'scheduled' || status === 'upcoming';
  const showEstimate = isNocturneTheme && isEstimate && isFutureSchedule;
  const showUpcoming = isNocturneTheme && !showEstimate && isFutureSchedule;
  const color =
    isNocturneTheme && status === 'paid'
      ? 'var(--nocturne-neutral-400)'
      : showEstimate || showUpcoming
        ? 'var(--nocturne-accent-100)'
        : statusProps.color;
  const backgroundColor =
    isNocturneTheme && status === 'paid'
      ? 'var(--nocturne-neutral-800)'
      : showEstimate || showUpcoming
        ? 'var(--nocturne-accent-800)'
        : statusProps.backgroundColor;
  const label =
    isNocturneTheme && status === 'paid'
      ? t('Posted')
      : showEstimate
        ? t('Estimate')
        : showUpcoming
          ? t('Upcoming')
          : titleFirst(getStatusLabel(status));
  const { Icon } = statusProps;

  return (
    <View
      style={{
        color,
        backgroundColor,
        padding: isNocturneTheme ? '4px 8px' : '6px 8px',
        borderRadius: isNocturneTheme ? 999 : 4,
        flexDirection: 'row',
        alignItems: 'center',
        flexShrink: 0,
        fontSize: isNocturneTheme ? 11 : undefined,
      }}
    >
      {!isNocturneTheme && (
        <Icon
          style={{
            width: 13,
            height: 13,
            marginRight: 7,
          }}
        />
      )}
      <Text style={{ lineHeight: '1em' }}>{label}</Text>
    </View>
  );
}
